import { useState, useEffect } from 'react';
import * as React from 'react';

import placements from '../../placements';
import isElementVisible from '../../utils/is-element-visible';
import type { TrackingLibrary } from '../../utils/tracking';
import withBraze from '../../utils/with-braze';

import { Styled } from './teaser-consumer.styles';

const PLACEMENT = placements.TEASER;

interface TeaserImageProps {
  onCardShown: () => void;
  onCardClicked: () => void;
  imageUrl: string;
  linkText: string;
  url: string;
  portalContainerRef: React.RefObject<HTMLDivElement>;
  openTarget: string;
}

const TeaserImage = ({
  onCardShown,
  onCardClicked,
  imageUrl,
  linkText,
  url,
  portalContainerRef,
  openTarget,
}: TeaserImageProps): JSX.Element => {
  const [adShown, setAdShown] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const resizeHandler = (): void => {
    if (
      !adShown &&
      portalContainerRef.current &&
      isElementVisible(portalContainerRef.current)
    ) {
      setAdShown(true);
      window.removeEventListener('resize', resizeHandler);
    }
  };

  useEffect(() => {
    if (adShown) onCardShown();
  }, [adShown]);

  useEffect(() => {
    if (
      imageLoaded &&
      portalContainerRef.current &&
      isElementVisible(portalContainerRef.current)
    ) {
      setAdShown(true);
      window.removeEventListener('resize', resizeHandler);
    }
  }, [imageLoaded]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  return (
    <Styled.Container>
      <a
        data-qa="teaser-link"
        target={openTarget}
        rel="noopener noreferrer"
        href={url}
        onClick={onCardClicked}
      >
        <Styled.Image
          onLoad={() => setImageLoaded(true)}
          src={imageUrl}
          alt={linkText}
          title={linkText}
        />
      </a>
    </Styled.Container>
  );
};

interface TeaserPayload {
  campaignId: string;
  imageUrl: string;
  linkText: string;
  url: string;
  openTarget: string;
}

interface TeaserProps {
  payload: TeaserPayload;
  section: string;
  trackingLibrary?: TrackingLibrary;
}

const Teaser = ({
  payload: { campaignId, imageUrl, linkText, url, openTarget },
  trackingLibrary,
}: TeaserProps): JSX.Element => {
  const portalContainerRef = React.useRef<HTMLDivElement>(null);

  const onCardShown = (): void => {
    trackingLibrary?.trackCardImpression(PLACEMENT, campaignId);
  };

  const onCardClicked = (): void => {
    trackingLibrary?.trackCardClick(PLACEMENT, campaignId);
  };

  return (
    <div ref={portalContainerRef} data-qa="teaser-portal-container">
      <TeaserImage
        portalContainerRef={portalContainerRef}
        url={url}
        imageUrl={imageUrl}
        linkText={linkText}
        onCardShown={onCardShown}
        onCardClicked={onCardClicked}
        openTarget={openTarget}
      />
    </div>
  );
};

const TeaserConsumer = withBraze(PLACEMENT)(Teaser);
export { TeaserConsumer, Teaser, TeaserProps };
